import { Controller } from "@hotwired/stimulus";

// GO4S Design System — Groupes repliables du menu latéral.
// Chaque groupe (« Structure », « Élèves »…) s'ouvre et se replie d'un clic
// sur son titre ; l'état est mémorisé par module (localStorage). Au départ
// tout est replié, sauf le groupe de la page en cours, toujours ouvert au
// chargement. Le bouton d'en-tête
// ouvre ou replie tout ; Alt + clic sur un titre n'ouvre que ce groupe.
// Sidebar réduite (icônes seules) : tout reste visible, géré en CSS.
const PREFIX = "ds-navgroups:";

export default class extends Controller {
  static targets = ["group", "toggleAll"];
  static values = { key: String };

  connect() {
    const saved = this._read();
    this.groupTargets.forEach((group) => {
      const active = group.querySelector(".ds-navitem.is-active") !== null;
      const open = active || saved[group.dataset.key] === true;
      this._apply(group, open);
    });
    this._refreshToggleAll();
    // Les transitions ne jouent qu'après le premier rendu (pas d'animation au chargement).
    requestAnimationFrame(() => this.element.classList.add("is-ready"));
  }

  toggle(event) {
    const group = event.currentTarget.closest("[data-ds-navgroups-target='group']");
    if (!group) return;

    if (event.altKey) {
      this.groupTargets.forEach((other) => this._apply(other, other === group));
    } else {
      this._apply(group, !this._isOpen(group));
    }
    this._save();
    this._refreshToggleAll();
  }

  toggleAll() {
    const open = !this._allOpen();
    this.groupTargets.forEach((group) => this._apply(group, open));
    this._save();
    this._refreshToggleAll();
  }

  _isOpen(group) {
    return !group.classList.contains("is-closed");
  }

  _allOpen() {
    return this.groupTargets.every((group) => this._isOpen(group));
  }

  _apply(group, open) {
    group.classList.toggle("is-closed", !open);
    const head = group.querySelector(".ds-navgroup__head");
    const body = group.querySelector(".ds-navgroup__body");
    if (head) head.setAttribute("aria-expanded", String(open));
    // Replié : ses liens sortent de l'ordre de tabulation.
    if (body) body.toggleAttribute("inert", !open);
    group.classList.toggle("has-active", group.querySelector(".ds-navitem.is-active") !== null);
  }

  _refreshToggleAll() {
    if (!this.hasToggleAllTarget) return;

    const allOpen = this._allOpen();
    const button = this.toggleAllTarget;
    button.classList.toggle("is-all-open", allOpen);
    const label = allOpen ? button.dataset.collapseLabel : button.dataset.expandLabel;
    button.setAttribute("title", label);
    button.setAttribute("aria-label", label);
  }

  _read() {
    try {
      return JSON.parse(localStorage.getItem(PREFIX + this.keyValue)) || {};
    } catch (_) {
      return {};
    }
  }

  _save() {
    const state = {};
    this.groupTargets.forEach((group) => { state[group.dataset.key] = this._isOpen(group); });
    try { localStorage.setItem(PREFIX + this.keyValue, JSON.stringify(state)); } catch (_) { /* quota/privé */ }
  }
}
